import { Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import PreferenceItem from "./PreferenceItem";
import SelectDropdown from "./SelectDropdown";
import classNames from "classnames";
import { CloseRounded, HelpOutlineRounded } from "@mui/icons-material";
import { setShowWarning, updateChatPreferences } from "../../redux/slices/responseCustomizationSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import LoadingButton from "../Shared/LoadingButton";
import { showNotification } from "../../redux/slices/notificationSlice";
import { areArraysEqual } from "../../helpers";
import { defaultPreferences, InfoRichnessType, initialSectorsArray, LvlOfExpTypes, MarketCapTypes } from "./constants";
import { logClickEvent } from "../../analytics";
import { useDurationTracker } from "../../hooks/useDurationTracker";

type CustomizeSettingsModalProps = {
  handleClose: () => void;
  sessionId: string;
};
const CustomizeSettingsModal = ({ handleClose, sessionId }: CustomizeSettingsModalProps) => {
  const [infoRichness, setInfoRichness] = useState(InfoRichnessType.EXPLANATORY);
  const [lvlOfExp, setLvlOfExp] = useState(LvlOfExpTypes.ENTHUSIAST);
  const [marketCap, setMarketCap] = useState(MarketCapTypes.ANY);
  const [includedSectors, setIncludedSectors] = useState<string[]>([]);
  const [excludedSectors, setExcludedSectors] = useState<string[]>([]);

  const { showSettings, savedSessionPreferences, isUpdating } = useSelector((state: { responseCustomization }) => state.responseCustomization);
  useDurationTracker(showSettings, "customize_response_duration");

  const savedPreference = savedSessionPreferences[sessionId];
  // populate form with last saved settings, if any. if none, use default settings
  useEffect(() => {
    if (savedPreference) {
      setInfoRichness(savedPreference.information_richness || InfoRichnessType.EXPLANATORY);
      setLvlOfExp(savedPreference.expertise_level || LvlOfExpTypes.ENTHUSIAST);
      setMarketCap(savedPreference.market_cap || MarketCapTypes.ANY);

      setIncludedSectors(savedPreference.include_sectors || []);
      setExcludedSectors(savedPreference.exclude_sectors || []);
      ;
    }
  }, [savedPreference]);

  const makeOptionAvailable = useCallback((sectorLabel: string) => {
    setIncludedSectors(prev => prev.filter(label => label !== sectorLabel));
    setExcludedSectors(prev => prev.filter(label => label !== sectorLabel));
  }, []);

  const resetSelectedOptions = useCallback((isInclude: boolean) => {
    if (isInclude) {
      setIncludedSectors([]);
    }
    else {
      setExcludedSectors([]);
    }
  }, []);

  const resetDefaultOptions = () => {
    setInfoRichness(defaultPreferences.information_richness);
    setLvlOfExp(defaultPreferences.expertise_level);
    setMarketCap(defaultPreferences.market_cap);
    setIncludedSectors([]);
    setExcludedSectors([]);
  };

  // compare current selection to last saved selection (or default preferences)
  const isMatchingPreferences = useCallback(
    (preferences = defaultPreferences) => {
      return (
        (infoRichness === preferences.information_richness)
        && (lvlOfExp === preferences.expertise_level)
        && (marketCap === preferences.market_cap)
        && areArraysEqual(
          includedSectors,
          preferences.include_sectors,
        )
        && areArraysEqual(
          excludedSectors,
          preferences.exclude_sectors,
        )
      );
    },
    [infoRichness, lvlOfExp, marketCap, includedSectors, excludedSectors],
  );

  const modalRef = useRef(null);
  const dropdownRefInclSectors = useRef(null);
  const dropdownRefExclSectors = useRef(null);

  const dispatch = useDispatch<AppDispatch>();

  const onClose = useCallback(() => {
    if (isMatchingPreferences(savedPreference)) {
      handleClose();
    }
    else {
      dispatch(setShowWarning(true));
    }
  }, [isMatchingPreferences, savedPreference]);

  const handleClickOutside = useCallback((event) => {
    const isOutsideModal = modalRef.current && !modalRef.current.contains(event.target);
    if (isOutsideModal && (!dropdownRefInclSectors.current && !dropdownRefExclSectors.current)) {
      onClose();
    }
  }, [onClose]);

  // tracks for clicks outside the form
  useEffect(() => {
    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings, handleClose, handleClickOutside]);

  if (!showSettings) return null;

  const handleSaveChatPreferences = async () => {
    const preferences = {
      information_richness: infoRichness,
      expertise_level: lvlOfExp,
      market_cap: marketCap,
      include_sectors: includedSectors,
      exclude_sectors: excludedSectors,
    };

    logClickEvent("customize_response_save", { preferences });

    try {
      await dispatch(
        updateChatPreferences({
          session_id: sessionId,
          preferences,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          message: "Response customization saved. TradeGPT will now respond based on your customization.",
          severity: "success",
          horizontal: "right",
        }),
      );
    }
    catch (error) {
      console.error("Failed to update chat preferences:", error);
    }
  };

  return (
    <div
      ref={modalRef}
      className="bg-white dark:bg-background/paper-elevation-1
                 w-modal-sm sm:w-full w-min-[312px] max-w-[720px]
                  rounded-lg sm:border sm:border-elevation-outlined"
    >
      <div className="bg-tgpt-secondary-accentLight h-full">
        <div className="h-full flex flex-col justify-between items-center p-6 gap-y-4 sm:gap-y-8">
          <div className="flex items-start">
            <Typography variant="h6/medium-500" className="dark:text-white">How would you like TradeGPT to respond?</Typography>
            <IconButton className="!h-8 !w-8 sm:!hidden" onClick={() => onClose()}>
              <CloseRounded className=" text-mui-black-38 dark:text-mui-white-38" />
            </IconButton>
          </div>
          <form className="flex flex-col gap-y-6 sm:gap-y-4 sm:w-full">
            <PreferenceItem
              stateVar={infoRichness}
              setter={setInfoRichness}
              prefType="Information Richness"
              options={["concise", "balanced", "explanatory"]}
            />
            <PreferenceItem
              stateVar={lvlOfExp}
              setter={setLvlOfExp}
              prefType="Your Level of Expertise"
              options={["expert", "enthusiast", "novice"]}
            />
            <PreferenceItem
              stateVar={marketCap}
              setter={setMarketCap}
              prefType="Market Cap"
              options={["large", "medium", "small", "any"]}
              toolTipContent="Your preference for company size, to gauge related risks"
            />
            <div className="flex flex-col gap-y-2 sm:gap-y-[12px]">
              <div className="flex flex-col gap-y-2 sm:flex-row justify-between">
                <div>
                  <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70 pe-1">
                    Sector of Interest
                  </Typography>
                  <Tooltip
                    placement="right"
                    title={<p className="text-center text-xs">Included sectors will be considered more heavily, while excluded sectors will be considered lightly.</p>}
                    classes={{
                      tooltip: "!w-[242px]",
                    }}
                  >
                    <IconButton>
                      <HelpOutlineRounded className="text-mui-black-60 dark:text-mui-white-70 opacity-60 !w-4 !h-4" />
                    </IconButton>
                  </Tooltip>
                </div>

                <SelectDropdown
                  dropdownRef={dropdownRefInclSectors}
                  options={initialSectorsArray}
                  selectedOptions={includedSectors}
                  onSelect={(selectedOptions) => {
                    setIncludedSectors(selectedOptions);
                  }}
                  disabledOptions={excludedSectors}
                  placeholder="Include Sectors"
                  includeSector={true}
                  limit={3}
                  makeOptionAvailable={makeOptionAvailable}
                  resetSelectedOptions={resetSelectedOptions}
                />
              </div>
              <div className="flex justify-end">
                <SelectDropdown
                  dropdownRef={dropdownRefExclSectors}
                  options={initialSectorsArray}
                  selectedOptions={excludedSectors}
                  onSelect={(selectedOptions) => {
                    setExcludedSectors(selectedOptions);
                  }}
                  disabledOptions={includedSectors}
                  placeholder="Exclude Sectors"
                  limit={3}
                  makeOptionAvailable={makeOptionAvailable}
                  resetSelectedOptions={resetSelectedOptions}
                />
              </div>
            </div>
          </form>

          <div className="flex gap-x-4 pt-6 w-full sm:justify-end">
            <Button
              variant="outlined"
              color="secondary"
              size="medium"
              className="w-1/2 sm:w-auto !border-tgpt-secondary-states-outlinedBorder sm:!border-none !text-transform-none !text-tgpt-secondary-main disabled:!text-mui-black-38 disabled:!text-mui-white-38"
              onClick={resetDefaultOptions}
              disabled={isMatchingPreferences(defaultPreferences)}
            >
              Reset to default
            </Button>
            <LoadingButton
              isLoading={isUpdating}
              onClick={handleSaveChatPreferences}
              variant="contained"
              size="medium"
              className={classNames(
                "w-1/2 sm:w-auto",
                "!text-transform-none",
                "dark:!text-white",
                "!bg-tgpt-secondary-main",
                "disabled:dark:!text-mui-white-38",
                "disabled:!bg-action-disabled-background",
              )}
              isDisabled={isMatchingPreferences(savedPreference) || isUpdating}
            >
              Save
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizeSettingsModal;
